<template>
  <div style="padding-top: 6px">
    <!-- #159 -->
    <!-- @formatter:off -->
    <p v-if="texts.priceInformation" class="padding-after" v-html="texts.priceInformation"/> <!-- njsscan-ignore: vue_template -->
    <!-- @formatter:on -->

    <dl class="parent">
      <dt>
        {{ priceLabel }}:
        <!-- #34 -->
        <small v-if="!isExemptedFromTax">
          <br class="only-no-mobile" />{{ $t('common.all_prices_incl_vat') }}
          <!-- #322 -->
        </small>
      </dt>

      <dd>
        <dl v-if="!isString(texts.promotionPriceOverrideText) && price && price.promotion" class="child">
          <dt v-if="price.promotion.durationFactor">
            <!-- #319: e.g. "Im 1. Monat" / "1. Woche bis Xte Woche" --->
            {{ promotionDuration }}:
          </dt>
          <dt v-else>
            <!-- #319: zB: Bis zum Ende des Aktionszeitraums am 31.12.2015: -->
            {{ $t('common.in_promo_until') }} <span class="text-nowrap">{{ price.promotion.promotionEnd }}:</span>
          </dt>

          <!-- #320 -->
          <dd v-if="!price.promotion.priceDisplay">
            <strong class="text-nowrap">{{ $t('common.free_of_charge') }}</strong>
          </dd>
          <dd v-else>
            <strong class="text-nowrap">
              {{ price.promotion.priceDisplay }}
            </strong>
            <span v-if="price.chargingIntervalOverrideText">
              {{ price.chargingIntervalOverrideText }}
            </span>
            <template v-else>
              {{ chargingInterval(price.chargingIntervalUnit, price.chargingIntervalFactor) }}
            </template>
            <strong v-if="promotionTypeOptin">
              {{ $t('common.optin_promotion_period_indicator') }}
              <!-- #321 -->
            </strong>
          </dd>
        </dl>
        <template v-else>
          <!-- @formatter:off -->
          <p v-if="texts.promotionPriceOverrideText" v-html="texts.promotionPriceOverrideText" /> <!-- njsscan-ignore: vue_template -->
          <!-- @formatter:on -->
        </template>

        <dl v-if="!isString(texts.regularPriceOverrideText)" class="child">
          <dt v-if="price && price.promotion">
            <!-- #323: e.g. "Ab dem 4. Monat" / "Danach gilt der reguläre Preis von" -->
            {{ promotionDurationAfter }}:
          </dt>

          <!-- #324 -->
          <dd v-if="!price || !price.priceDisplay">
            <strong class="text-nowrap">{{ $t('common.free_of_charge') }}</strong>
          </dd>
          <dd v-else>
            <strong class="text-nowrap">
              {{ price.priceDisplay }}
            </strong>
            <span v-if="price.chargingIntervalOverrideText">
              {{ price.chargingIntervalOverrideText }}
            </span>
            <template v-else-if="subscriptionType !== 2">
              {{ chargingInterval(price.chargingIntervalUnit, price.chargingIntervalFactor) }}
            </template>
          </dd>
        </dl>
        <template v-else>
          <!-- #323 -->
          <!-- @formatter:off -->
          <p v-if="texts.regularPriceOverrideText" v-html="texts.regularPriceOverrideText" /> <!-- njsscan-ignore: vue_template -->
          <!-- @formatter:on -->
        </template>
      </dd>

      <template v-if="isAnySetupPrice">
        <dt>
          {{ $t('common.price_label_onetime_setup') }}:
          <!-- #340 -->
          <small v-if="!isExemptedFromTax">
            <br class="only-no-mobile" />{{ $t('common.all_prices_incl_vat_onetime') }}
            <!-- #343 -->
          </small>
        </dt>

        <dd>
          <dl class="child">
            <template v-if="isSetupPricePromo">
              <dt v-if="price.promotion.durationFactor">
                <!-- #341: e.g. "Im 1. Monat" (promotion.durationUnit) --->
                {{ fromFirstUnit(price.promotion.durationUnit, 'onetime') }}:
              </dt>
              <dt v-else>
                <!-- #341: e.g. "Im 1. Monat" (chargingIntervalUnit) --->
                {{ fromFirstUnit(price.chargingIntervalUnit, 'onetime') }}:
              </dt>

              <!-- #342 -->
              <dd>
                <strong class="text-nowrap">{{ price.promotion.setupPrice.priceDisplay }}</strong>
              </dd>
            </template>

            <template v-if="price && price.setupPrice">
              <dt>
                <!-- #344: e.g. "Im 4. Monat" -->
                {{ onetimeUnit }}:
              </dt>

              <!-- #345 -->
              <dd>
                <strong class="text-nowrap">{{ price.setupPrice.priceDisplay }}</strong>
              </dd>
            </template>
          </dl>
        </dd>
      </template>

      <template v-if="showMinimumCommitmentPeriod">
        <!-- #325 -->
        <dt> {{ $t('common.minimum_contract_period_label') }}: </dt>

        <!-- #326 -->
        <!-- @formatter:off -->
        <dd v-if="isString(texts.minimumCommitmentPeriodOverrideText)" v-html="texts.minimumCommitmentPeriodOverrideText"></dd> <!-- njsscan-ignore: vue_template -->
        <!-- @formatter:on -->
        <dd v-else-if="minimumCommitmentPeriodFactor > 0">
          {{ minimumContractPeriod }}
          <!-- @formatter:off -->
          <span v-html="texts.minimumCommitmentPeriodText" /> <!-- njsscan-ignore: vue_template -->
          <!-- @formatter:on -->
        </dd>
        <dd v-else>
          {{ $t('common.no_minimum_contract_period') }}
        </dd>
      </template>
    </dl>

    <!-- #327 -->
    <!-- @formatter:off -->
    <p v-if="texts.beforeOptinInfoText" class="padding-after" v-html="texts.beforeOptinInfoText" /> <!-- njsscan-ignore: vue_template -->
    <!-- @formatter:on -->
    <!-- #313 -->
    <!-- @formatter:off -->
    <p v-else-if="texts.afterOptinInfoText" class="padding-after" v-html="texts.afterOptinInfoText" /> <!-- njsscan-ignore: vue_template -->
    <!-- @formatter:on -->
  </div>
</template>

<script>
import {mapGetters} from 'vuex';
import {isEmptyString, isString} from '@/utils/string';
import {chargingInterval, fromFirstUnit, unitToString} from '@/utils/price';

export default {
  name: 'PriceDetails',

  props: {
    price: {
      type: Object,
      required: false,
      default: null,
    },

    texts: {
      type: Object,
      required: true,
    },

    promotionTypeOptin: {
      type: Boolean,
      required: true,
    },

    subscriptionType: {
      type: Number,
      required: false,
      default: 0,
    },
  },

  computed: {
    ...mapGetters('contract', ['isExemptedFromTax']),

    /**
     * e.g. "Monatliche Kosten"
     * @returns {VueI18n.TranslateResult}
     */
    priceLabel() {
      if (!this.price || !this.price.chargingIntervalUnit) return this.$t('common.price');
      else if (this.subscriptionType === 2) return this.$t('common.price_label_onetime');
      return this.$t(`common.price_label_${unitToString(this.price.chargingIntervalUnit)}`);
    },

    /**
     * e.g. "Im 1. Monat" / "1. bis 3. Monat"
     * @returns {VueI18n.TranslateResult}
     */
    promotionDuration() {
      const unit = this.price.promotion.durationUnit;
      const duration = this.price.promotion.durationFactor.toString();

      if (duration === '1') {
        return fromFirstUnit(unit);
      } // else: durationFactor > 1 (function is only called if durationFactor is set!)

      return this.$t(`common.first_to_x_${unitToString(unit)}`, { duration });
    },

    promotionDurationAfter() {
      const unit = this.price?.promotion?.durationUnit;
      let duration = this.price?.promotion?.durationFactor;

      if (duration) {
        duration += 1;
        return this.$t(`common.from_x_${unitToString(unit)}`, { duration });
      }

      return this.$t('common.in_regular_period');
    },

    isAnySetupPrice() {
      return this.price?.setupPrice || this.isSetupPricePromo;
    },

    isSetupPricePromo() {
      return !!this.price?.promotion?.setupPrice;
    },

    onetimeUnit() {
      let duration = this.price?.promotion?.durationFactor;

      if (duration) {
        const unit = this.price?.promotion?.durationUnit;
        duration += 1;
        return this.$t(`common.from_x_${unitToString(unit)}_onetime`, { duration });
      }

      const unit = this.price.chargingIntervalUnit;
      return fromFirstUnit(unit, 'onetime');
    },

    /**
     * Because price can be null, we have to compute the property
     */
    minimumCommitmentPeriodFactor() {
      return this.price?.minimumCommitmentPeriodFactor;
    },

    minimumContractPeriod() {
      const unit = this.price.minimumCommitmentPeriodUnit;
      const factor = this.minimumCommitmentPeriodFactor;

      return `${factor} ${this.$t(`common.${unitToString(unit, factor)}`)}`;
    },

    showMinimumCommitmentPeriod() {
      return this.subscriptionType !== 2 && !this.isEmptyString(this.texts.minimumCommitmentPeriodOverrideText);
    },
  },

  methods: {
    isString,
    isEmptyString,
    fromFirstUnit,
    chargingInterval,
  },
};
</script>

<style scoped lang="scss">
@import '~@scss';

p.padding-after {
  padding-top: 0;
  padding-bottom: 12px;
}

dl {
  padding: 0;
  margin: 0;
}

dl::before,
dl::after {
  display: table;
  content: ' ';
}

dl::after {
  clear: both;
}

dl > dd {
  margin: 0;
}

dl p,
.child {
  padding-top: 0;
  padding-bottom: 0;
  margin-top: 0;
  margin-bottom: 0;
}

br.only-no-mobile {
  display: none;
}

.parent > dd {
  padding-bottom: 6px;
}

@media screen and (min-width: 800px) {
  dl p {
    padding-bottom: 6px;
  }

  .child {
    display: flex;
    flex-wrap: wrap;
  }

  .child > dt {
    width: 200px;
    padding-right: 24px;
    padding-bottom: 6px;
  }

  .child > dd {
    width: calc(100% - 212px);
    margin-left: 0;
  }

  br.only-no-mobile {
    display: block;
  }

  .parent {
    display: flex;
    flex-wrap: wrap;
  }

  .parent > dt {
    width: 41.66668%;
    padding-bottom: 12px;
  }

  .parent > dd {
    width: calc(58.33334% - 12px);
    margin-left: 12px;
  }
}

@media screen and (max-width: 800px) {
  .child > dt {
    float: left;
    padding-right: 6px;
  }

  .parent > dt {
    padding-bottom: 6px;
  }

  .parent > dd {
    padding-bottom: 12px;
  }

  .parent > dt > small {
    font-weight: normal;
  }
}

.parent > dt {
  font-weight: 600;
}

.parent > dt > small {
  font-weight: normal;
}

/*
  @media screen and (max-width: #{$grid-large-max}px) {
    .child > dt {
      width: 200px;
      padding-bottom: 6px;
      padding-right: 24px;
    }
    .child > dd {
      margin-left: 6px;
      width: calc(100% - 212px)
    }
  }
  */
</style>
